import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  useBankServiceCapabilitiesByIfsc,
  useCreateMandate,
  useInvestorBankAccounts,
} from "creso-core/hooks";
import type { EMandateBankCapabilities } from "creso-core/api";
import { MandateType } from "creso-core/constants";
import type { Mandate } from "creso-core/types";
import {
  Button,
  Form,
  FormField,
  FormPrefixedInput,
  FormRadioGroup,
  FormSelect,
  cn,
} from "web-design-system";
import { Check, X } from "lucide-react";
import { handleError } from "../../utils/error";

const MANDATE_TYPES = [
  { value: "E", label: "eNach" },
  { value: "X", label: "Physical" },
  { value: "U", label: "UPI (Soon)", disabled: true },
];

const MANDATE_LIMITS = [10000, 50000, 100000, 500000, 1000000];

const BANK_AUTH_METHODS: {
  label: string;
  key: keyof Pick<
    EMandateBankCapabilities,
    | "emandate_netbanking_enabled"
    | "emandate_debit_card_enabled"
    | "emandate_aadhaar_enabled"
  >;
}[] = [
  { label: "Net Banking", key: "emandate_netbanking_enabled" },
  { label: "Debit Card", key: "emandate_debit_card_enabled" },
  { label: "Aadhaar", key: "emandate_aadhaar_enabled" },
];

const mandateSchema = z.object({
  amount: z.number().min(1, "Amount must be greater than 0"),
  mandateType: z.enum(["E", "X", "U"]),
  bankAccount: z.string().min(1, "Bank account is required"),
});

type FormValues = z.infer<typeof mandateSchema>;

export type CreateMandateFormState = {
  fields: React.ReactNode;
  submit: () => void;
  isSubmitting: boolean;
  /** False when there is no verified bank account to mandate against. */
  canSubmit: boolean;
};

interface Props {
  investmentAccountId: string;
  /**
   * Runs once the mandate exists. Everything after creation — authorising,
   * notifying, confirmation messaging, where to go next — belongs to the host.
   * Awaited, so the form stays submitting until it settles.
   */
  onCreate: (mandate: Mandate) => void | Promise<void>;
  /**
   * Renders the fields inside the host's own chrome. Partners wraps them in its
   * page layout with the submit button in that layout's action slot; the
   * clients dialog renders them with the buttons inline underneath.
   */
  children: (state: CreateMandateFormState) => React.ReactNode;
}

export function CreateMandateForm({
  investmentAccountId,
  onCreate,
  children,
}: Props) {
  const { data: bankAccounts, isLoading: isLoadingBankAccounts } =
    useInvestorBankAccounts(investmentAccountId);

  const verifiedBankAccounts =
    bankAccounts?.filter(
      (account) => account.verification_status === "verified"
    ) ?? [];

  const bankAccountOptions = verifiedBankAccounts.map((account) => ({
    label: `${account.bank_name} *${account.account_number.slice(-4)}`,
    value: String(account.id),
  }));

  const { mutateAsync: createMandate } = useCreateMandate();

  const form = useForm<FormValues>({
    resolver: zodResolver(mandateSchema),
    values: {
      amount: 0,
      mandateType: "E",
      bankAccount: bankAccountOptions[0]?.value ?? "",
    },
  });

  const mandateType = form.watch("mandateType");
  const selectedBankAccount = form.watch("bankAccount");
  const showBankAuthMethods =
    mandateType === "E" && Boolean(selectedBankAccount);

  const selectedBankIfsc = verifiedBankAccounts.find(
    (account) => String(account.id) === selectedBankAccount
  )?.ifsc_code;

  const { data: bankCapabilities, isLoading: isLoadingBankCapabilities } =
    useBankServiceCapabilitiesByIfsc(selectedBankIfsc ?? "", {
      enabled: showBankAuthMethods && !!selectedBankIfsc,
    });

  const bankAuthMethods = bankCapabilities
    ? BANK_AUTH_METHODS.map(({ label, key }) => ({
        label,
        supported: bankCapabilities[key],
      }))
    : [];

  const onSubmit = async (values: FormValues) => {
    let mandate: Mandate;
    try {
      mandate = await createMandate({
        investment_account_guid: investmentAccountId,
        mandate_type: values.mandateType as MandateType,
        bank_account_id: values.bankAccount,
        mandate_limit: values.amount,
      });
    } catch (error) {
      handleError(error, "Failed to create mandate");
      return;
    }

    await onCreate(mandate);
  };

  const hasBankAccount = isLoadingBankAccounts || bankAccountOptions.length > 0;

  const fields = (
    <div className="size-full flex flex-col">
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col gap-6"
        >
          <FormField
            control={form.control}
            name="mandateType"
            render={({ field }) => (
              <FormRadioGroup
                {...field}
                label="Select mandate type"
                options={MANDATE_TYPES}
                description={
                  mandateType === "E"
                    ? "Maximum value allowed ₹1Cr"
                    : "Maximum value allowed ₹1Cr. Takes 15-20 days to get approved."
                }
              />
            )}
          />
          <FormField
            control={form.control}
            name="bankAccount"
            render={({ field }) => (
              <FormSelect
                {...field}
                label="Bank Account"
                options={bankAccountOptions}
                placeholder={
                  isLoadingBankAccounts
                    ? "Loading bank accounts..."
                    : "Select bank account"
                }
                disabled={isLoadingBankAccounts}
              />
            )}
          />

          {showBankAuthMethods ? (
            <div className="rounded-lg p-3 -mt-4 flex flex-col gap-2 bg-slate-50 dark:bg-slate-800">
              <p className="text-sm text-tag-slate-foreground">
                This bank allows authorisation by:
              </p>
              <div className="flex flex-wrap gap-4">
                {isLoadingBankCapabilities ? (
                  <p className="text-sm text-muted-foreground">Loading...</p>
                ) : (
                  bankAuthMethods.map((method) => (
                    <div
                      key={method.label}
                      className="flex items-center gap-1.5 text-sm"
                    >
                      {method.supported ? (
                        <Check className="size-4 text-tag-slate-foreground" />
                      ) : (
                        <X className="size-4 text-muted-foreground" />
                      )}
                      <span
                        className={cn(
                          method.supported
                            ? "text-tag-slate-foreground"
                            : "text-muted-foreground"
                        )}
                      >
                        {method.label}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          ) : null}

          <FormField
            control={form.control}
            name="amount"
            render={({ field }) => (
              <FormPrefixedInput
                {...field}
                label="Mandate Amount"
                type="number"
                prefix=""
                readOnly={false}
                value={field.value}
                onChange={(value) => field.onChange(Number(value))}
              >
                ₹ &nbsp;
              </FormPrefixedInput>
            )}
          />

          <div className="flex flex-wrap gap-2 -mt-3">
            {MANDATE_LIMITS.map((amount) => (
              <Button
                key={amount}
                variant="outline"
                size="sm"
                type="button"
                onClick={() => {
                  form.setValue("amount", amount);
                  form.trigger("amount");
                }}
              >
                ₹{amount.toLocaleString("en-IN")}
              </Button>
            ))}
          </div>

          {!hasBankAccount && (
            <p className="text-xs text-muted-foreground -mt-3">
              No verified bank account found. A mandate can only be set up
              against one.
            </p>
          )}
        </form>
      </Form>
    </div>
  );

  return children({
    fields,
    submit: form.handleSubmit(onSubmit),
    isSubmitting: form.formState.isSubmitting,
    canSubmit: hasBankAccount,
  });
}
